import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { logErrorTelemetry } from "@/hooks/useErrorTelemetry";
import { STALE_TIME_30_SECONDS, STALE_TIME_1_MINUTE, STALE_TIME_5_MINUTES, STALE_TIME_1_HOUR, STALE_TIME_24_HOURS } from "@/lib/query-constants";
import { isSupabasePermissionError, isSupabaseSchemaError } from "@/lib/supabase-compat";

/**
 * Logs query/mutation errors to telemetry.
 * Fire-and-forget - errors in logging don't affect the app.
 */
function logQueryError(error: unknown, context: string, meta?: Record<string, unknown>) {
  const err = error as Error;
  const errorMessage = err?.message || String(error);
  const errorStack = err?.stack;

  // Don't log expected errors (auth redirects, not found, etc.)
  const skipPatterns = ["jwt", "unauthorized", "not found", "PGRST116", "lock broken", "lock acquired by another"];
  if (skipPatterns.some(p => errorMessage.toLowerCase().includes(p.toLowerCase()))) {
    return;
  }

  if (isSupabaseSchemaError(error) || isSupabasePermissionError(error)) {
    return;
  }

  logErrorTelemetry({
    errorMessage: `[${context}] ${errorMessage}`,
    errorName: err?.name ?? null,
    route: typeof window !== "undefined" ? window.location.pathname : "unknown",
    errorStack,
    metadata: {
      context,
      queryMeta: meta,
    },
  });
}

/**
 * Determines if an error is retryable.
 * - Network errors and 5xx server errors are retryable
 * - 4xx client errors (auth, not found, validation) are not retryable
 * - Specific Supabase/Postgres errors that indicate permanent failures are not retryable
 */
export function shouldRetryError(error: unknown): boolean {
  if (!error) return false;

  // Network errors - always retry.
  // Browsers throw TypeError("Failed to fetch") while iOS WKWebView throws
  // DOMException("Load failed") or TypeError("Load failed") for network issues.
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return true;
  }
  const errMsg = (error as { message?: string })?.message?.toLowerCase() ?? "";
  if (errMsg === "load failed" || errMsg.includes("network request failed") || errMsg.includes("the internet connection appears to be offline")) {
    return true;
  }

  // Check for error object with status or code
  const err = error as { status?: number; code?: string; message?: string };

  // HTTP status-based retry logic
  if (typeof err.status === "number") {
    // Don't retry 4xx client errors (auth failures, not found, validation errors)
    if (err.status >= 400 && err.status < 500) {
      return false;
    }
    // Retry 5xx server errors
    if (err.status >= 500) {
      return true;
    }
  }

  // Supabase/Postgres error codes that should NOT be retried
  const nonRetryableCodes = [
    "PGRST202",
    "PGRST205",
    "PGRST116", // No rows found (not an error, just empty result)
    "PGRST301", // Row-level security violation
    "23505",    // Duplicate key violation
    "23503",    // Foreign key violation
    "22P02",    // Invalid text representation
    "42501",    // Insufficient privilege
    "42P01",    // Undefined table
    "42703",    // Undefined column
  ];

  if (err.code && nonRetryableCodes.includes(err.code)) {
    return false;
  }

  if (isSupabaseSchemaError(error) || isSupabasePermissionError(error)) {
    return false;
  }

  // Check for auth-related error messages (non-retryable).
  // Use multi-word phrases to avoid false-positive matches on messages
  // like "database session pool exhausted" or "cache entry expired".
  const authErrorPatterns = [
    "jwt expired",
    "jwt invalid",
    "token expired",
    "invalid token",
    "refresh token",
    "not authenticated",
    "authentication required",
    "session expired",
    "session revoked",
    "invalid credentials",
    "unauthorized",
    "insufficient privilege",
  ];
  if (err.message) {
    const lowerMessage = err.message.toLowerCase();
    if (authErrorPatterns.some((pattern) => lowerMessage.includes(pattern))) {
      return false;
    }
  }

  // Default: retry for unknown errors (could be transient)
  return true;
}

/**
 * Calculate retry delay with exponential backoff.
 * Attempts: 1s, 2s, 4s (capped)
 */
function getRetryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 4000);
}

/** A promise that never settles: parks whatever awaits it for good. */
const never = () => new Promise<never>(() => {});

/**
 * A mutation cache whose mutations never reach their request once the client is retired.
 * TanStack's retryer asks the cache's canRun() before calling mutationFn (and before each retry),
 * and pauses until it is allowed. So a mutation whose onMutate was still awaiting when its user
 * went (awaiting cancelQueries, say, which the retiring clear() itself resolves) stays paused
 * instead of sending its request under the next user's session (Codex on #7027). The page reload
 * on a user change (src/lib/reload-on-user-change.ts) starts a navigation but doesn't stop the
 * current task, so this has to hold on its own until the unload.
 */
class RetirableMutationCache extends MutationCache {
  constructor(
    config: ConstructorParameters<typeof MutationCache>[0],
    private readonly isRetired: () => boolean,
  ) {
    super(config);
  }

  canRun(mutation: Parameters<MutationCache["canRun"]>[0]): boolean {
    return !this.isRetired() && super.canRun(mutation);
  }
}

/**
 * Builds the app's QueryClient. There is deliberately no shared instance: each signed-in user gets
 * a client of their own from src/lib/user-scoped-query-client.tsx, so nothing one user loaded (or
 * a late callback of theirs writes) can answer for the next person on the tab (#7022).
 *
 * `isRetired` reports that the user this client belonged to has gone. A mutation of theirs still
 * in flight then never reaches its own callbacks: TanStack awaits the cache-level onSuccess and
 * onError below before a mutation's own (useMutation's onSuccess/onError/onSettled), and a retired
 * client's never settle. So a late completion can't toast the previous user's records, navigate,
 * or send a follow-up request under the next user's session, and code awaiting mutateAsync stays
 * parked with it. Callbacks passed to mutate() itself already stop with the unmounted observer.
 */
export const createQueryClient = (options: { isRetired?: () => boolean } = {}): QueryClient => {
  const isRetired = options.isRetired ?? (() => false);
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Log query errors after all retries have been exhausted
        logQueryError(error, "Query", {
          // Keys and hashes can contain credentials or user input. Never emit either.
          queryKeyLength: query.queryKey.length,
        });
      },
    }),
    mutationCache: new RetirableMutationCache({
      // Removed redundant ensureFreshSession() preflight from onMutate —
      // auth-js's `autoRefreshToken: true` already handles token refresh
      // on a 30s tick. The preflight here was doubling auth-lock
      // acquisitions and producing the cosmetic [Query] AbortError:
      // Lock broken errors that PR #1402 filtered from telemetry.
      // Investigation 2 Option B (this PR) removes the underlying
      // contention at the source.
      onSuccess: () => (isRetired() ? never() : undefined),
      onError: (error, variables, context, mutation) => {
        if (isRetired()) return never();
        // Log all mutation errors to telemetry
        logQueryError(error, "Mutation", {
          mutationKeyLength: mutation.options.mutationKey?.length,
          // Don't log full variables as they may contain sensitive data
          variablesType: typeof variables,
        });
      },
    }, isRetired),
    defaultOptions: {
      queries: {
        staleTime: STALE_TIME_5_MINUTES,
        retry: (failureCount, error) => {
          // Max 3 retries
          if (failureCount >= 3) return false;
          return shouldRetryError(error);
        },
        retryDelay: getRetryDelay,
      },
    },
  });
};
